import { Fragment } from 'react';

import { useRecoilState } from 'recoil';

import { Dialog, Transition } from '@headlessui/react';
import { XIcon } from '@heroicons/react/outline';
import { IconFormer } from '@shared/components/IconsFormer';
import { simpleOpenAtomFamily } from '@shared/state/atom';
import { classNames } from '@shared/utils/joinClassNamesUtils';
import { Link, useLocation } from 'react-router-dom';

const navigation = [
  {
    name: 'Home',
    href: '/gift-seller/home',
    icon: IconFormer.Home,
    active: true,
  },
  {
    name: '주문검색',
    href: '/gift-seller/order-search',
    icon: IconFormer.MagnifyingGlassRegular,
    active: true,
  },
  {
    name: '발송 대기',
    href: '/gift-seller/coupon-redemption',
    icon: IconFormer.Courtesy,
    active: true,
  },
  {
    name: '쿠폰 내역 검색',
    href: '/gift-seller/coupon-history',
    icon: IconFormer.MagnifyingGlassRegular,
    active: true,
  },
  {
    name: '배송 관리',
    href: '/gift-seller/delivery-management',
    icon: IconFormer.DeliveryBlue,
    active: true,
  },
  {
    name: '상품 브랜드',
    href: '/gift-seller/item-brand',
    icon: IconFormer.OtherApps,
    active: true,
  },
  {
    name: '선택유닛 관리',
    href: '/gift-seller/choice-unit',
    icon: IconFormer.CheckBox,
    active: true,
  },
  {
    name: '교환권 테마',
    href: '/gift-seller/coupon-theme',
    icon: IconFormer.Setting,
    active: true,
  },
  {
    name: '원상품 관리',
    href: '/gift-seller/item-info',
    icon: IconFormer.Flower,
    active: true,
  },
  {
    name: '상조별 상품관리',
    href: '/gift-seller/item',
    icon: IconFormer.FlowerBlue,
    active: true,
  },
];

const secondaryNavigation = [
  {
    name: '이벤트 로그',
    href: '/gift-seller/event-log',
    icon: IconFormer.View,
  },
  {
    name: '설정',
    href: '/gift-seller/settings',
    icon: IconFormer.Setting,
  },
];

export const GiftSellerSidebar = function GiftSellerSidebar() {
  const { pathname } = useLocation();
  const [sidebarOpen, setSidebarOpen] = useRecoilState(
    simpleOpenAtomFamily('AdvancedSidebar-gift-seller'),
  );

  return (
    <>
      {/* Mobile sidebar */}
      <Transition.Root show={sidebarOpen} as={Fragment}>
        <Dialog
          as="div"
          className="fixed inset-0 z-40 flex lg:hidden"
          onClose={setSidebarOpen}
        >
          <Transition.Child
            as={Fragment}
            enter="transition-opacity ease-linear duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="transition-opacity ease-linear duration-300"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-600 bg-opacity-75" />
          </Transition.Child>
          <Transition.Child
            as={Fragment}
            enter="transition ease-in-out duration-300 transform"
            enterFrom="-translate-x-full"
            enterTo="translate-x-0"
            leave="transition ease-in-out duration-300 transform"
            leaveFrom="translate-x-0"
            leaveTo="-translate-x-full"
          >
            <div className="relative flex w-full max-w-xs flex-1 flex-col bg-indigo-700 pt-5 pb-4">
              <Transition.Child
                as={Fragment}
                enter="ease-in-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in-out duration-300"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div className="absolute top-0 right-0 -mr-12 pt-2">
                  <button
                    type="button"
                    className="ml-1 flex h-10 w-10 items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                    onClick={() => setSidebarOpen(false)}
                  >
                    <span className="sr-only">Close sidebar</span>
                    <XIcon className="h-6 w-6 text-white" aria-hidden="true" />
                  </button>
                </div>
              </Transition.Child>
              <div className="flex flex-shrink-0 items-center px-4">
                <Link to="/gift-seller/home">
                  <p className="h-8 text-16 font-medium text-white">답례품 관리</p>
                </Link>
              </div>
              <nav
                className="mt-5 h-full flex-shrink-0 divide-y divide-indigo-800 overflow-y-auto"
                aria-label="Sidebar"
              >
                <div className="space-y-1 px-2">
                  {navigation
                    .filter((item) => item.active)
                    .map((item) => (
                      <Link
                        key={item.name}
                        to={item.href}
                        onClick={() => setSidebarOpen(false)}
                        className={classNames(
                          pathname === item.href || pathname.startsWith(item.href + '/')
                            ? 'bg-indigo-800 text-white hover:text-white'
                            : 'text-indigo-100 hover:bg-indigo-600 hover:text-white',
                          'group flex items-center rounded-md px-2 py-2 text-base font-medium',
                        )}
                      >
                        <item.icon
                          className="wh-6 mr-4 flex-shrink-0 fill-indigo-200"
                          aria-hidden="true"
                        />
                        {item.name}
                      </Link>
                    ))}
                </div>
                <div className="mt-6 pt-6">
                  <div className="space-y-1 px-2">
                    {secondaryNavigation.map((item) => (
                      <Link
                        key={item.name}
                        to={item.href}
                        onClick={() => setSidebarOpen(false)}
                        className="group flex items-center rounded-md px-2 py-2 text-base font-medium text-indigo-100 hover:bg-indigo-600 hover:text-white"
                      >
                        <item.icon
                          className="wh-6 mr-4 fill-indigo-200"
                          aria-hidden="true"
                        />
                        {item.name}
                      </Link>
                    ))}
                  </div>
                </div>
              </nav>
            </div>
          </Transition.Child>
          <div className="w-14 flex-shrink-0" aria-hidden="true" />
        </Dialog>
      </Transition.Root>

      {/* Static sidebar for desktop */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-grow flex-col overflow-y-auto bg-indigo-700 pt-5 pb-4">
          <div className="flex flex-shrink-0 items-center px-4">
            <Link to="/gift-seller/home">
              <p className="h-8 text-16 font-medium text-white">답례품 관리</p>
            </Link>
          </div>
          <nav
            className="mt-5 flex flex-1 flex-col divide-y divide-indigo-200 overflow-y-auto"
            aria-label="Sidebar"
          >
            <div className="space-y-1 px-2">
              {navigation.map((item) => {
                if (item.active)
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={classNames(
                        pathname === item.href || pathname.startsWith(item.href + '/')
                          ? 'bg-indigo-800 text-white hover:text-white'
                          : 'text-indigo-100 hover:bg-indigo-600 hover:text-white',
                        'group flex items-center rounded-md px-2 py-2 text-sm font-medium leading-6',
                      )}
                    >
                      <item.icon
                        className="wh-6 mr-4 flex-shrink-0 fill-indigo-200"
                        aria-hidden="true"
                      />
                      {item.name}
                    </Link>
                  );
              })}
            </div>
            <div className="mt-6 pt-6">
              <div className="space-y-1 px-2">
                {secondaryNavigation.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className="group flex items-center rounded-md px-2 py-2 text-sm font-medium leading-6 text-indigo-100 hover:bg-indigo-600 hover:text-white"
                  >
                    <item.icon
                      className="wh-6 mr-4 fill-indigo-200 text-indigo-200"
                      aria-hidden="true"
                    />
                    {item.name}
                  </Link>
                ))}
              </div>
            </div>
          </nav>
        </div>
      </div>
    </>
  );
};
